/**
 * Viewport orchestrator hook.
 *
 * Owns the single source of truth (the external `ViewportStore`), the
 * imperative commands that make up the board's public navigation API
 * (dive / pop / navigate / reset / flyTo / centre / restore), and the
 * automatic refit effects. The actual input listeners — wheel, drag,
 * keyboard — and the animation loop live in composable hooks
 * (./viewportHooks.ts) so each is independently testable.
 *
 * All commands read the live viewport synchronously via `store.get()`,
 * do any DOM measurement up-front, and commit a pure value with
 * `store.set()` — no side effects inside a state updater.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { BoardItem, BoardSpace, SpaceLike } from "@archlang/scene/board";
import { asSource } from "@archlang/scene/board";

/** Walk the tree until we find the space directly containing `moduleId`.
 *  Returns the focal path (list of space ids from root) and the target item. */
function findInTree(
  space: BoardSpace,
  moduleId: string,
  path: string[] = [],
): { path: string[]; item: BoardItem } | null {
  const here = [...path, space.id];
  for (const item of space.items) {
    // A `replicated` copy resolves by its logical key too, so revealing a
    // selected copy (selection stores the logical id) frames it rather than
    // no-op'ing. (Framing the WHOLE doppelgänger group is a future refinement.)
    if (item.id === moduleId || item.logicalId === moduleId) return { path: here, item };
    if (item.space) {
      const nested = findInTree(item.space, moduleId, here);
      if (nested) return nested;
    }
  }
  return null;
}
import {
  type Viewport,
  clampPanRoam,
  focalRectsForPath,
  frameItem,
  initialViewport,
  planPairFrame,
  popFocal,
  pushFocal,
  resolveFocal,
  walkFocal,
} from "@archlang/scene/board";
import { diveBodyRect } from "./bodyRect";
import { observeResize } from "./sharedResizeObserver";
import { createViewportStore, useViewportValue, type ViewportStore } from "./viewportStore";
import {
  useDragPan,
  useKeyboardNav,
  useTouchGestures,
  useViewportAnim,
  useWheelZoom,
  type GestureDeps,
} from "./viewportHooks";

export interface ViewportControls {
  /** The underlying external store — for surfaces that need per-frame camera
   *  reads WITHOUT riding the React render (e.g. the minimap viewport rect
   *  subscribes to this directly so the memoized minimap scene never
   *  re-renders during a gesture). */
  readonly store: ViewportStore;
  readonly viewport: Viewport;
  readonly focal: BoardSpace;
  readonly diveInto: (item: BoardItem) => void;
  /** Animated zoom-to-frame for an item in the current focal — the leaf
   *  counterpart of `diveInto` (double-click "bring into view"). */
  readonly frameOn: (item: BoardItem) => void;
  readonly popOut: () => void;
  readonly navigateTo: (path: readonly string[]) => void;
  readonly resetView: () => void;
  /**
   * Re-fit the CURRENT focal to the container's current size, preserving the
   * focal path (unlike `resetView`, which always pops to the root). For host-
   * driven reshapes — switching the editor/board split orientation — where the
   * one-shot mount fit is already locked but the pane changed shape, so the
   * existing camera would leave the content off-screen. A divider DRAG must NOT
   * call this (the user is deliberately keeping their view); a layout SWITCH
   * should, because only the host can tell the two apart.
   */
  readonly refit: () => void;
  readonly flyTo: (moduleId: string) => void;
  /** Navigate INTO a space by id: sets the focal path to the space itself so
   *  the board shows its contents (the rooted-tab counterpart of `flyTo`, which
   *  only frames the node within its containing space). No-op if the id can't
   *  be located. */
  readonly enterSpace: (moduleId: string) => void;
  /**
   * Restore an arbitrary viewport — used to back out of a sidebar
   * drill-down that changed the focal/pan/zoom. Animates when the focal
   * path matches; jumps otherwise (cross-focal lerp is meaningless).
   */
  readonly restoreViewport: (vp: Viewport) => void;
  /**
   * Immediate, non-animated viewport replacement. Used by the diff
   * visualizer's pan/zoom mirror — animating would lag the slave pane
   * behind the user's input.
   */
  readonly setViewportImmediate: (vp: Viewport) => void;
  /** Centre the viewport on a point in the current focal's coordinates. */
  readonly centerOn: (fx: number, fy: number) => void;
  /**
   * Frame a connection's two endpoint nodes together (the step-scrubber's
   * "locate this step"). Ancestor rules live in `planPairFrame`: cross-branch
   * pairs frame from the common ancestor space (outside), a child↔parent
   * pair frames inside the parent. Animates within the current focal; jumps
   * for a cross-focal target. No-op when an endpoint can't be resolved.
   */
  readonly framePair: (aId: string, bId: string) => void;
  readonly screenSize: () => { width: number; height: number };
  /**
   * Animate from the live viewport to `target` over the standard duration.
   * Exposed so the camera operator can drive smooth, interruptible motion
   * through the same rAF loop the dive/pop commands use. For a cross-focal
   * target use `setViewportImmediate` instead — lerping across focal
   * coordinate spaces is meaningless.
   */
  readonly animateTo: (target: Viewport) => void;
}

export interface UseViewportOptions {
  /**
   * Optional restored viewport (e.g. from localStorage). When provided,
   * the hook uses it as the initial state and skips the "fit to screen"
   * pass that runs on first mount — restored state already encodes the
   * user's intended view, and re-fitting would yank them away from it.
   */
  readonly initialViewport?: Viewport;
  /**
   * Handle wheel events for pan/zoom. Default true. Set false for
   * embedded previews where wheeling over the canvas should scroll the
   * page instead of panning/zooming the diagram.
   */
  readonly wheel?: boolean;
  /**
   * Master interactivity switch. Default true. `false` = embed posture: the
   * wheel / drag / touch / keyboard hooks AND the out-of-bounds rescue timer
   * all stay inert — no window/document listeners attach, no idle timer arms —
   * so the board is a live, projected, but non-interactive preview. Independent
   * of `wheel` / `keyboard` (which stay meaningful when interactive);
   * `interactive:false` ANDs over them, forcing every input path off at the
   * hook level. Many such embeds mount over one shared model (the catalog).
   */
  readonly interactive?: boolean;
  /**
   * Externally-owned viewport store. When provided, this hook drives and
   * reads that store instead of creating its own — letting several boards
   * (the side-by-side diff) share one viewport. The owner is responsible
   * for the initial fit, so the auto-fit / structure-refit effects are
   * skipped in this mode.
   */
  readonly store?: ViewportStore;
  /**
   * Bind the global (window) keyboard shortcuts. Default true. Set false
   * on all-but-one board when sharing a `store`, so a keypress isn't
   * applied once per board to the same store.
   */
  readonly keyboard?: boolean;
  /**
   * Resolve a module id to the focal path (space ids) of the space that
   * CONTAINS it. Model-derived, so it works on the lazy board where the
   * target may live below the solved preview depth (the tree-walk fallback
   * only sees what's materialised). Returns null if unknown.
   */
  readonly moduleFocalPath?: (moduleId: string) => readonly string[] | null;
  /**
   * Identity of the loaded workspace. When this value changes (a new file
   * set is imported), the hook discards any prior pan/zoom/dive intent and
   * re-fits the root to the new content — otherwise the board stays parked
   * wherever the user left the *previous* workspace, which reads as a blank
   * canvas when the new content lands off-screen. Compared by `Object.is`,
   * so a fresh object/string/number per load triggers the re-fit.
   */
  readonly resetKey?: unknown;
  /**
   * Fired on real manual board input (wheel / drag / keyboard) — NOT on
   * programmatic commands. The camera operator uses it to back off briefly so
   * its auto-motion never fights a hand on the board.
   */
  readonly onUserGesture?: () => void;
  /**
   * A touch double-tap landed on a board item: open it (dive into a space /
   * zoom-frame a leaf) — the touch counterpart of desktop double-click.
   * Return false when the id can't be resolved; the gesture then falls back
   * to a plain zoom-in step at the tap point. Pass a STABLE function (read
   * changing state through a ref) — it lands in a listener effect's deps.
   */
  readonly onItemDoubleTap?: (id: string) => boolean;
}

export function useViewport(
  root: SpaceLike,
  containerRef: React.RefObject<HTMLElement | null>,
  options: UseViewportOptions = {},
): ViewportControls {
  // `root` may be a fully-solved tree (diff / embed) or a lazy engine-backed
  // SpaceSource (the live board, which solves only the focal subtree). Address
  // it uniformly through a SpaceSource; `rootSpace` is the root space itself,
  // used for the initial fit / reset framing.
  const source = useMemo(() => asSource(root), [root]);
  const rootSpace = source.at([]);

  // Single source of truth. Prefer an externally-supplied store (shared
  // across diff panes); otherwise create one, once, and reuse it.
  const ownStoreRef = useRef<ViewportStore | null>(null);
  if (ownStoreRef.current === null && !options.store) {
    ownStoreRef.current = createViewportStore(
      options.initialViewport ?? {
        focalPath: [rootSpace.id],
        center: { x: rootSpace.originX + rootSpace.width / 2, y: rootSpace.originY + rootSpace.height / 2 },
        zoom: 1,
      },
    );
  }
  const store = options.store ?? ownStoreRef.current!;
  const viewport = useViewportValue(store);
  // The store owner handles fitting; a board attached to an external store
  // never auto-fits or re-fits (that would fight the owner / the peer pane).
  const autoFit = options.store === undefined;

  // Keep a stable ref to the current root so the wheel / pointer / key
  // listeners don't have to depend on it. Re-binding listeners on every
  // root change (layout solver recreates the BoardSpace each render)
  // dropped wheel events that landed during the re-attach gap — making
  // a continuous pinch gesture feel like a single discrete step.
  const rootRef = useRef<SpaceLike>(source);
  rootRef.current = source;

  // Auto-fit is a *one-shot* request, not a standing behaviour. A plain data
  // refresh — editing the loaded workspace, an SSE patch on the website — must
  // never move the viewport; only the initial mount and a genuine workspace
  // swap (resetKey change) may frame the content. `wantFitRef` is that
  // pending-fit token: armed on mount / reset, consumed once a non-empty layout
  // is framed. `userNavigatedRef` lets a user gesture cancel a still-pending fit
  // (set by the gesture hooks + commands below). A caller-restored viewport
  // counts as user intent already, so no fit is requested on mount.
  const wantFitRef = useRef(options.initialViewport === undefined);
  const userNavigatedRef = useRef(options.initialViewport !== undefined);
  const initializedRef = useRef(options.initialViewport !== undefined);

  const screenSize = useCallback(() => {
    const el = containerRef.current;
    if (!el) return { width: 0, height: 0 };
    const r = el.getBoundingClientRect();
    return { width: r.width, height: r.height };
  }, [containerRef]);

  const { animateTo, scheduleAnimateTo, cancelAnim } = useViewportAnim(store, screenSize);

  // ---- Initial fit: settle on the container size, then lock ----
  // The fit must be a *one-shot* (a later deliberate panel resize must NOT snap
  // the board back to the root). But consuming it on the very first
  // ResizeObserver callback is unreliable: that callback can land at a transient
  // container size (entrance / flex / font / nav-panel settling), locking the
  // board to a half-measured, off-centre frame — the "not reliably centred on
  // first open" bug.
  //
  // So while the fit is pending we re-frame the root on each callback (the board
  // tracks the container as it settles), but defer the LOCK by one quiet frame:
  // each fit (re)arms a rAF that clears `wantFitRef`. A burst of mount-time size
  // changes keeps re-arming it, so the lock lands on the final settled size; a
  // single-callback mount locks one frame later.
  //
  // Once locked, a resize is a NO-OP on the stored viewport: it is centre-
  // canonical, so the render re-derives pan from the new size and the on-screen
  // centre is held automatically (sides change, centre stays) — no per-resize
  // re-pan needed. The only active work is a `clampPan` pass so a big shrink
  // doesn't leave a zoomed-out space drifting past the edge; `clampPan` returns
  // the same object when in-bounds, so the `store.set` no-ops in the common case.
  useEffect(() => {
    if (!autoFit) return;
    const el = containerRef.current;
    if (!el) return;
    let settleRaf = 0;
    const dispose = observeResize(el, () => {
      const { width, height } = screenSize();
      if (width === 0 || height === 0) return; // hidden / not yet measured

      // Pending mount/reset fit (and the user hasn't grabbed the camera): frame
      // the root as the container settles, then lock one quiet frame later.
      if (wantFitRef.current && !userNavigatedRef.current) {
        if (rootSpace.items.length === 0) return; // nothing real to frame yet
        store.set(initialViewport(rootSpace, width, height));
        initializedRef.current = true;
        // Lock one quiet frame after the last size change. Re-arm on each fit so
        // a settling burst locks on its final size, not the first transient.
        if (settleRaf) cancelAnimationFrame(settleRaf);
        settleRaf = requestAnimationFrame(() => {
          settleRaf = 0;
          wantFitRef.current = false;
        });
        return;
      }

      // Locked / user-owned: centre is invariant under resize; only re-clamp.
      const cur = store.get();
      store.set(clampPanRoam(source, cur, { width, height }));
    });
    return () => {
      if (settleRaf) cancelAnimationFrame(settleRaf);
      dispose();
    };
  }, [autoFit, root, containerRef, screenSize, store]);

  // Consume a *pending* fit (mount / workspace swap) once the layout's
  // structure lands — covers the async-compile case where the new model
  // arrives a render after resetKey changed. Without a pending fit this is a
  // no-op, so editing the loaded workspace never moves the viewport. Gated on
  // the item *set*, not placement tweaks, so dragging a node can't trip it.
  // Structure of the root space (+ any solved nested levels). Walks whatever
  // the source has materialised — for the lazy engine that's the root focal +
  // preview depth, which is the right granularity to re-fit on.
  const structureKey = useMemo(() => {
    const ids: string[] = [];
    const walk = (s: BoardSpace) => {
      for (const it of s.items) {
        ids.push(it.id);
        if (it.space) walk(it.space);
      }
    };
    walk(rootSpace);
    return ids.sort().join("|");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root]);
  const prevStructureRef = useRef<string>(structureKey);
  useEffect(() => {
    if (!autoFit) return;
    if (!initializedRef.current) return;
    if (!wantFitRef.current) return;
    if (userNavigatedRef.current) return;
    if (prevStructureRef.current === structureKey) return;
    prevStructureRef.current = structureKey;
    const { width, height } = screenSize();
    if (width === 0 || height === 0) return;
    store.set(initialViewport(rootSpace, width, height));
    if (rootSpace.items.length > 0) wantFitRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFit, structureKey, root, screenSize, store]);

  // ---- Re-fit on workspace change ----
  // A new import is not "navigation" — the user's prior pan/zoom belonged to
  // the old workspace. Clear that intent so the new content gets framed, and
  // fit immediately. The new layout may arrive a render later (async compile);
  // the structure-refit effect above (now ungated) catches that case too.
  const prevResetKeyRef = useRef(options.resetKey);
  useEffect(() => {
    if (!autoFit) return;
    if (Object.is(prevResetKeyRef.current, options.resetKey)) return;
    prevResetKeyRef.current = options.resetKey;
    // A genuine workspace/space switch. If the host remembers a viewport for the
    // new space (per-space memory), RESTORE it — atomically here, so the pending
    // fit and the restore can't race the async model landing. Restoring counts
    // as user intent and cancels the fit, so the structure/RO effects won't
    // reframe when the new model arrives a render later.
    const remembered = options.initialViewport;
    if (remembered) {
      wantFitRef.current = false;
      userNavigatedRef.current = true;
      cancelAnim();
      store.set(remembered);
      return;
    }
    // No memory: arm a fresh fit and drop prior gesture intent (the old pan/zoom
    // belonged to the old space). The new layout may arrive a render later; the
    // structure effect consumes the still-pending fit when it lands, so we don't
    // clear `wantFitRef` here.
    wantFitRef.current = true;
    userNavigatedRef.current = false;
    const { width, height } = screenSize();
    if (width === 0 || height === 0) return;
    store.set(initialViewport(rootSpace, width, height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFit, options.resetKey, root, screenSize, store]);

  // ---- Commands ----
  const diveInto = useCallback(
    (item: BoardItem) => {
      if (!item.space) return;
      userNavigatedRef.current = true;
      const framed = frameItem(store.get(), item, screenSize(), 0.02);
      // Animate to fully framed, then rebase on the PURE solver slot rect —
      // the same rect the nested preview renders at (`bodyRect.ts`), valid at
      // any zoom (no DOM read, no collapsed-band null, no deferred frame).
      animateTo(framed, () => {
        store.set((v) => pushFocal(v, item, diveBodyRect(item)));
      });
    },
    [animateTo, screenSize, store],
  );

  /** Animated "bring into view" for an item in the CURRENT focal — the
   *  double-click action for leaf modules (no nested space to dive into). */
  const frameOn = useCallback(
    (item: BoardItem) => {
      userNavigatedRef.current = true;
      const framed = frameItem(store.get(), item, screenSize(), 0.25);
      scheduleAnimateTo(framed);
    },
    [scheduleAnimateTo, screenSize, store],
  );

  const popOut = useCallback(() => {
    const current = store.get();
    if (current.focalPath.length <= 1) return;
    const steps = walkFocal(source, current.focalPath);
    const container = steps[steps.length - 1]?.containerItem;
    if (!container) return;
    userNavigatedRef.current = true;
    cancelAnim();
    // Anchor the rebase on the pure solver slot rect — the rect the nested
    // preview will render at once popped, so the pop is visually identical.
    // (`popFocal` prefers the rect stored by the matching push; this is the
    // fallback for a restored focal stack.)
    const measured = diveBodyRect(container);
    // Pop instantly (visually identical), then animate the parent into view.
    const popped = popFocal(current, container, measured);
    store.set(popped);
    const target = frameItem(popped, container, screenSize(), 0.3);
    scheduleAnimateTo(target);
  }, [source, scheduleAnimateTo, cancelAnim, screenSize, store]);

  const navigateTo = useCallback(
    (path: readonly string[]) => {
      userNavigatedRef.current = true;
      cancelAnim();
      const focal = resolveFocal(source, path);
      const size = screenSize();
      const vp = initialViewport(focal, size.width, size.height);
      store.set({ ...vp, focalPath: path, focalRects: focalRectsForPath(source, path) });
    },
    [source, screenSize, cancelAnim, store],
  );

  const resetView = useCallback(() => {
    userNavigatedRef.current = false;
    cancelAnim();
    const { width, height } = screenSize();
    store.set(initialViewport(rootSpace, width, height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, screenSize, cancelAnim, store]);

  const refit = useCallback(() => {
    const { width, height } = screenSize();
    if (width === 0 || height === 0) return; // container hidden / not measured
    cancelAnim();
    // Re-frame whatever is currently focal (root if not drilled in) to the new
    // container shape, keeping the focal path so a layout switch doesn't also
    // pop the user out of a space they'd navigated into.
    const cur = store.get();
    const path = cur.focalPath ?? [];
    const focalSpace = resolveFocal(source, path);
    const vp = initialViewport(focalSpace, width, height);
    store.set({ ...vp, focalPath: path, focalRects: focalRectsForPath(source, path) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, screenSize, cancelAnim, store]);

  /** Navigate the viewport to focus on a module by id, wherever it lives in
   *  the tree. Sets focal path to the containing space and frames the item. */
  const flyTo = useCallback(
    (moduleId: string) => {
      // Prefer the model-derived path (works below the solved preview depth);
      // fall back to walking whatever the source has materialised.
      const modelPath = options.moduleFocalPath?.(moduleId) ?? null;
      let path: readonly string[];
      let item: BoardItem | undefined;
      if (modelPath) {
        path = modelPath;
        item = resolveFocal(source, modelPath).items.find((it) => it.id === moduleId);
      } else {
        const found = findInTree(rootSpace, moduleId);
        if (!found) return;
        path = found.path;
        item = found.item;
      }
      if (!item) return;
      userNavigatedRef.current = true;
      cancelAnim();
      const focal = resolveFocal(source, path);
      const size = screenSize();
      const found = { path, item };
      const base: Viewport = {
        focalPath: found.path,
        focalRects: focalRectsForPath(source, found.path),
        center: { x: 0, y: 0 },
        zoom: 1,
      };
      // Start by centring the focal space so the frame math below is stable.
      const centred = initialViewport(focal, size.width, size.height);
      const framed = frameItem(
        { ...base, zoom: centred.zoom, center: centred.center },
        found.item,
        size,
        0.25,
      );
      store.set(framed);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, cancelAnim, screenSize, store],
  );

  const enterSpace = useCallback(
    (moduleId: string) => {
      // The containing focal path (same resolution as flyTo); diving in =
      // appending the space id so the board's focal becomes the space itself.
      const containing =
        options.moduleFocalPath?.(moduleId) ?? findInTree(rootSpace, moduleId)?.path ?? null;
      if (!containing) return;
      // focalPath segments are SPACE ids, not module ids (the `Viewport.focalPath`
      // contract; `pushFocal` appends `item.space.id`). Append the module's OWN
      // space id — a raw moduleId here breaks the strict `walkFocal` match, which
      // silently disables pop-out, the gravity-well context/roam, and (on the
      // eager diff/embed source) resolves the focal to root instead of the space.
      navigateTo([...containing, `${moduleId}::space`]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigateTo, source],
  );

  const centerOn = useCallback(
    (fx: number, fy: number) => {
      userNavigatedRef.current = true;
      cancelAnim();
      // Centre-canonical: place the focal point directly under the screen
      // centre — no size needed.
      store.set((current) => ({ ...current, center: { x: fx, y: fy } }));
    },
    [cancelAnim, store],
  );

  const framePair = useCallback(
    (aId: string, bId: string) => {
      const focalPathOf = (id: string): readonly string[] | null =>
        options.moduleFocalPath?.(id) ?? findInTree(rootSpace, id)?.path ?? null;
      const plan = planPairFrame(source, aId, bId, store.get(), screenSize(), focalPathOf);
      if (!plan) return;
      userNavigatedRef.current = true;
      cancelAnim();
      if (plan.sameFocal) animateTo(plan.viewport);
      else store.set(plan.viewport);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, screenSize, cancelAnim, animateTo, store],
  );

  const restoreViewport = useCallback(
    (vp: Viewport) => {
      userNavigatedRef.current = true;
      // Cancel any pending auto-fit: a restored viewport is intentional, and the
      // mount/ResizeObserver fit (which checks only `wantFit`, not navigation)
      // would otherwise clobber it when an async model lands a render later.
      wantFitRef.current = false;
      cancelAnim();
      const cur = store.get();
      const samePath =
        cur.focalPath.length === vp.focalPath.length &&
        cur.focalPath.every((id, i) => id === vp.focalPath[i]);
      if (samePath) {
        animateTo(vp);
      } else {
        store.set(vp);
      }
    },
    [animateTo, cancelAnim, store],
  );

  const setViewportImmediate = useCallback(
    (vp: Viewport) => {
      userNavigatedRef.current = true;
      cancelAnim();
      store.set(vp);
    },
    [cancelAnim, store],
  );

  // ---- Input listeners (each a self-contained, testable hook) ----
  const gestureDeps: GestureDeps = {
    store,
    containerRef,
    rootRef,
    userNavigatedRef,
    cancelAnim,
    onUserGesture: options.onUserGesture,
  };
  // Live count of touch pointers — links the touch hook to the Safari
  // gesture-event mute inside useWheelZoom (iOS pinch fires both streams).
  const touchCountRef = useRef(0);
  // Master interactivity gate (embed posture). Threads into every input hook
  // and the OOB timer, so a non-interactive embed attaches ZERO window/document
  // listeners and arms NO idle timer. ANDs over `wheel` / `keyboard`.
  const interactive = options.interactive ?? true;
  useWheelZoom(gestureDeps, options.wheel, touchCountRef, interactive);
  useDragPan(gestureDeps, interactive);
  useTouchGestures(
    gestureDeps,
    {
      animateTo,
      onItemDoubleTap: options.onItemDoubleTap,
      touchCountRef,
    },
    interactive,
  );
  useKeyboardNav(gestureDeps, { popOut }, interactive && (options.keyboard ?? true));

  // Memoised: `resolveFocal` walks the tree, so recompute only when the
  // root or the focal path actually changes — not on every pan/zoom frame.
  const focal = useMemo(() => resolveFocal(source, viewport.focalPath), [source, viewport.focalPath]);

  // ---- Out-of-bounds rescue ("teleport back in") ----
  // The gesture pipelines clamp every frame, but a viewport can still land
  // outside the space border through other doors: a restored viewport from
  // a different screen size, a rotation, an interrupted flight. Debounced
  // past the animation cadence so it only ever fires on a SETTLED view —
  // then it snaps the pan back inside. `clampPan` returns the same object
  // when in bounds, so the common case is a no-op.
  useEffect(() => {
    if (!interactive) return; // embed posture: no idle rescue ticker
    const t = setTimeout(() => {
      const { width, height } = screenSize();
      if (width === 0 || height === 0) return;
      const vp = store.get();
      const clamped = clampPanRoam(source, vp, { width, height });
      if (clamped !== vp) store.set(clamped);
    }, 400);
    return () => clearTimeout(t);
  }, [interactive, viewport, source, screenSize, store]);

  return { store, viewport, focal, diveInto, frameOn, popOut, navigateTo, resetView, refit, flyTo, enterSpace, restoreViewport, setViewportImmediate, centerOn, framePair, screenSize, animateTo };
}
